import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import SortIcon from '@mui/icons-material/Sort';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, ReactNode, useEffect, useRef, useState } from 'react';

type Order = 'asc' | 'desc';

export interface HeadCell<T> {
  id: keyof T | string;
  label: string;
  subtitle?: string;
  tooltip?: string;
  numeric: boolean;
  sortable: boolean;
  renderCell?: (item: T) => ReactNode;
  sortKey?: keyof T | string;
  filter?: ReactNode;
  width?: string; // Optional width property for fixed column widths
  sticky?: 'left' | 'right';
  getComparableValue?: (item: T) => string | number | null; // Function to get a comparable value for sorting
  sortOptions?: Array<{
    id: string;
    label: string;
    getComparableValue: (item: T) => string | number | null;
  }>;
}

export interface TableGroup<T> {
  id: string;
  label: string;
  getValue: (item: T) => string;
  valueLabels?: Record<string, string>;
  valueSubtitles?: Record<string, string>;
  valueOrder?: string[];
}

export const getGroupKey = <T,>(groups: TableGroup<T>[], path: string[], level: number): string =>
  JSON.stringify(groups.slice(0, level + 1).map((group, index) => [group.id, path[index]]));

export const getAllGroupKeys = <T,>(data: T[], groups: TableGroup<T>[]): Set<string> => {
  const keys = new Set<string>();
  if (groups.length === 0) return keys;
  for (const row of data) {
    const path = groups.map(group => group.getValue(row));
    for (let level = 0; level < groups.length; level++) {
      keys.add(getGroupKey(groups, path, level));
    }
  }
  return keys;
};

export interface SortableTableProps<T> {
  data: T[];
  headCells: HeadCell<T>[];
  defaultSortBy?: keyof T | string;
  defaultOrder?: Order;
  idAccessor: (item: T) => string | number;
  emptyMessage?: string;
  showFilters?: boolean;
  stickyHeader?: boolean;
  filters?: Record<string, any>; // Current active filters
  onClearFilter?: (filterId: string) => void; // Handler to clear a single filter
  onClearAllFilters?: () => void; // Handler to clear all filters
  getFilterLabel?: (filterId: string) => string; // Function to get filter label
  getFilterDescription?: (filterId: string, value: any) => string; // Function to get filter description
  totalCount?: number; // Total count of items (unfiltered)
  pagination?: boolean; // Whether to show pagination
  defaultRowsPerPage?: number; // Default number of rows per page
  selectable?: boolean;
  selectedIds?: Array<string | number>;
  onSelectionChange?: (ids: Array<string | number>) => void;
  isRowSelectable?: (item: T) => boolean;
  onVisibleIdsChange?: (ids: Array<string | number>) => void;
  groups?: TableGroup<T>[];
  collapsedGroups?: Set<string>;
  onCollapsedGroupsChange?: (collapsed: Set<string>) => void;
}

function getComparator<T>(
  order: Order,
  orderBy: keyof T | string,
  headCells: HeadCell<T>[]
): (a: T, b: T) => number {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy, headCells)
    : (a, b) => -descendingComparator(a, b, orderBy, headCells);
}

function descendingComparator<T>(
  a: T,
  b: T,
  orderBy: keyof T | string,
  headCells: HeadCell<T>[]
): number {
  const headCell = headCells.find(
    cell => cell.id === orderBy || cell.sortOptions?.some(option => option.id === orderBy)
  );
  const sortOption = headCell?.sortOptions?.find(option => option.id === orderBy);
  const sortKey = headCell?.sortKey || orderBy;

  // Use custom comparable value function if provided
  const getComparableValue = sortOption?.getComparableValue ?? headCell?.getComparableValue;
  if (getComparableValue) {
    const aValue = getComparableValue(a);
    const bValue = getComparableValue(b);

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (bValue < aValue) {
      return -1;
    }
    if (bValue > aValue) {
      return 1;
    }
    return 0;
  }

  const getNestedValue = (obj: any, path: string): any => {
    const keys = path.split('.');
    return keys.reduce((o, key) => (o ? o[key] : undefined), obj);
  };

  const aValue =
    typeof sortKey === 'string' && sortKey.includes('.')
      ? getNestedValue(a, sortKey as string)
      : a[sortKey as keyof T];

  const bValue =
    typeof sortKey === 'string' && sortKey.includes('.')
      ? getNestedValue(b, sortKey as string)
      : b[sortKey as keyof T];

  if (typeof aValue === 'string' && typeof bValue === 'string') {
    const aDate = new Date(aValue);
    const bDate = new Date(bValue);

    if (!isNaN(aDate.getTime()) && !isNaN(bDate.getTime())) {
      return bDate.getTime() - aDate.getTime();
    }
  }

  if (bValue < aValue) {
    return -1;
  }
  if (bValue > aValue) {
    return 1;
  }
  return 0;
}

export default function SortableTable<T>({
  data,
  headCells,
  defaultSortBy,
  defaultOrder = 'asc',
  idAccessor,
  emptyMessage = 'No data available',
  showFilters = false,
  stickyHeader = false,
  filters = {},
  onClearFilter,
  onClearAllFilters,
  getFilterLabel = id => id,
  getFilterDescription = (id, value) => String(value),
  totalCount,
  pagination = true,
  defaultRowsPerPage = 50,
  selectable = false,
  selectedIds,
  onSelectionChange,
  isRowSelectable,
  onVisibleIdsChange,
  groups = [],
  collapsedGroups: controlledCollapsedGroups,
  onCollapsedGroupsChange,
}: SortableTableProps<T>) {
  const [order, setOrder] = useState<Order>(defaultOrder);
  const [orderBy, setOrderBy] = useState<keyof T | string>(defaultSortBy || '');
  const [columnWidths, setColumnWidths] = useState<string[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null);
  const [sortMenuColumn, setSortMenuColumn] = useState<string | null>(null);
  const [uncontrolledCollapsedGroups, setUncontrolledCollapsedGroups] = useState<Set<string>>(
    new Set()
  );
  const isCollapsedControlled = controlledCollapsedGroups !== undefined;
  const collapsedGroups = isCollapsedControlled
    ? controlledCollapsedGroups
    : uncontrolledCollapsedGroups;

  const handleToggleGroupCollapse = (key: string) => {
    const next = new Set(collapsedGroups);
    if (next.has(key)) {
      next.delete(key);
    } else {
      next.add(key);
    }
    if (!isCollapsedControlled) {
      setUncontrolledCollapsedGroups(next);
    }
    onCollapsedGroupsChange?.(next);
  };

  const tableRef = useRef<HTMLTableElement>(null);
  const selectedSet = new Set<string | number>(selectedIds ?? []);
  const emitSelection = (next: Set<string | number>) => {
    onSelectionChange?.(Array.from(next));
  };
  const toggleRow = (id: string | number, item: T) => {
    if (isRowSelectable && !isRowSelectable(item)) return;
    const next = new Set(selectedSet);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    emitSelection(next);
  };

  // Track data length for pagination adjustments
  const dataLengthRef = useRef(data.length);
  const filtersRef = useRef(filters);
  const [currentPage, setCurrentPage] = useState(1);

  // Calculate pages based on current data
  const pageCount = pagination ? Math.ceil(data.length / rowsPerPage) : 1;

  // Update pagination when necessary conditions are met
  useEffect(() => {
    // Reset page when filters change
    const filtersChanged = JSON.stringify(filters) !== JSON.stringify(filtersRef.current);
    if (filtersChanged) {
      setCurrentPage(1);
      filtersRef.current = filters;
      return;
    }

    // Adjust page if current page would be invalid with new data length
    if (data.length !== dataLengthRef.current) {
      if (currentPage > pageCount) {
        setCurrentPage(Math.max(1, pageCount));
      }
      dataLengthRef.current = data.length;
    }
  }, [data.length, filters, currentPage, pageCount]);

  // Calculate column widths on initial render and when data changes
  useEffect(() => {
    if (tableRef.current && data.length > 0) {
      // Determine if we need to calculate widths (only do it if we don't have explicit widths)
      const needsCalculation = headCells.some(cell => !cell.width);

      if (needsCalculation) {
        // Wait for table to render with data
        setTimeout(() => {
          if (tableRef.current) {
            const headerRow = tableRef.current.querySelector('thead tr:first-child');

            if (headerRow) {
              const cells = Array.from(headerRow.querySelectorAll('th'));
              const widths = cells.map(cell => `${cell.clientWidth}px`);
              setColumnWidths(widths);
            }
          }
        }, 0);
      } else {
        // Use explicit widths
        setColumnWidths(headCells.map(cell => cell.width || 'auto'));
      }
    }
  }, [data, headCells]);

  const handleRequestSort = (property: keyof T | string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  const handleSelectSort = (property: keyof T | string, selectedOrder: Order) => {
    setOrderBy(property);
    setOrder(selectedOrder);
    setSortMenuAnchor(null);
    setSortMenuColumn(null);
  };

  const handleChangePage = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<{ value: unknown }>) => {
    setRowsPerPage(parseInt(event.target.value as string, 10));
    setCurrentPage(1);
  };

  const sortedData = orderBy ? [...data].sort(getComparator(order, orderBy, headCells)) : data;
  const groupedData =
    groups.length > 0
      ? [...sortedData].sort((left, right) => {
          for (const group of groups) {
            const leftValue = group.getValue(left);
            const rightValue = group.getValue(right);
            if (leftValue === rightValue) continue;
            const leftOrder = group.valueOrder?.indexOf(leftValue) ?? -1;
            const rightOrder = group.valueOrder?.indexOf(rightValue) ?? -1;
            if (leftOrder >= 0 || rightOrder >= 0) {
              if (leftOrder < 0) return 1;
              if (rightOrder < 0) return -1;
              return leftOrder - rightOrder;
            }
            return leftValue.localeCompare(rightValue);
          }
          return 0;
        })
      : sortedData;

  // Apply pagination
  const paginatedData = pagination
    ? groupedData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
    : groupedData;

  const hasActiveFilters = Object.keys(filters).length > 0;

  const selectableRows = isRowSelectable ? sortedData.filter(isRowSelectable) : sortedData;
  const visibleIds = selectableRows.map(idAccessor);
  const selectedVisibleCount = visibleIds.filter(id => selectedSet.has(id)).length;
  const allVisibleSelected = visibleIds.length > 0 && selectedVisibleCount === visibleIds.length;
  const someVisibleSelected = selectedVisibleCount > 0 && !allVisibleSelected;
  const handleToggleAll = () => {
    const next = new Set(selectedSet);
    if (allVisibleSelected) {
      visibleIds.forEach(id => next.delete(id));
    } else {
      visibleIds.forEach(id => next.add(id));
    }
    emitSelection(next);
  };
  const pageSelectableIds = paginatedData
    .filter(row => {
      if (isRowSelectable && !isRowSelectable(row)) return false;
      if (groups.length === 0) return true;
      const path = groups.map(group => group.getValue(row));
      return !path.some((_, level) => collapsedGroups.has(getGroupKey(groups, path, level)));
    })
    .map(idAccessor);
  const pageSelectableKey = pageSelectableIds.map(String).join(',');
  useEffect(() => {
    onVisibleIdsChange?.(pageSelectableIds);
    // pageSelectableIds is reconstructed each render; pageSelectableKey is the actual dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onVisibleIdsChange, pageSelectableKey]);
  const totalColumns = headCells.length + (selectable ? 1 : 0);
  const groupKey = (path: string[], level: number) => getGroupKey(groups, path, level);
  const groupCounts = new Map<string, number>();
  groupedData.forEach(row => {
    const path = groups.map(group => group.getValue(row));
    path.forEach((_, level) => {
      const key = groupKey(path, level);
      groupCounts.set(key, (groupCounts.get(key) ?? 0) + 1);
    });
  });
  let previousGroupPath: string[] = [];

  return (
    <Paper>
      <TableContainer>
        <Table ref={tableRef} aria-label="sortable table" stickyHeader={stickyHeader}>
          <TableHead>
            {showFilters && (
              <TableRow>
                {selectable && <TableCell padding="checkbox" />}
                {headCells.map((headCell, index) => (
                  <TableCell
                    key={`filter-${String(headCell.id)}`}
                    align={'left'}
                    sx={
                      headCell.sticky
                        ? {
                            position: 'sticky',
                            [headCell.sticky]: 0,
                            zIndex: 3,
                            bgcolor: 'background.paper',
                          }
                        : undefined
                    }
                    style={{
                      width: columnWidths[index] || headCell.width || 'auto',
                      minWidth:
                        headCell.width || (columnWidths[index] ? columnWidths[index] : '100px'),
                    }}
                  >
                    {headCell.filter}
                  </TableCell>
                ))}
              </TableRow>
            )}
            {showFilters && (
              <TableRow>
                <TableCell
                  colSpan={totalColumns}
                  sx={{ py: 1, borderBottom: '1px solid rgba(224, 224, 224, 1)' }}
                >
                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        Active Filters:
                      </Typography>
                      {hasActiveFilters ? (
                        Object.entries(filters).map(([filterId, value]) => (
                          <Chip
                            key={filterId}
                            label={`${getFilterLabel(filterId)}: ${getFilterDescription(filterId, value)}`}
                            onDelete={() => onClearFilter?.(filterId)}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Showing {paginatedData.length} of {sortedData.length}{' '}
                        {sortedData.length !== totalCount && totalCount !== undefined
                          ? `(filtered from ${totalCount})`
                          : ''}{' '}
                        items
                      </Typography>

                      <Button
                        variant="outlined"
                        startIcon={<FilterListOffIcon />}
                        onClick={onClearAllFilters}
                        size="small"
                        disabled={!hasActiveFilters}
                      >
                        Clear All Filters
                      </Button>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            <TableRow>
              {selectable && (
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    indeterminate={someVisibleSelected}
                    checked={allVisibleSelected}
                    disabled={visibleIds.length === 0}
                    onChange={handleToggleAll}
                    inputProps={{ 'aria-label': 'select all filtered rows' }}
                  />
                </TableCell>
              )}
              {headCells.map((headCell, index) => (
                <TableCell
                  key={String(headCell.id)}
                  align={'left'}
                  sx={
                    headCell.sticky
                      ? {
                          position: 'sticky',
                          [headCell.sticky]: 0,
                          zIndex: 4,
                          bgcolor: 'background.paper',
                          boxShadow:
                            headCell.sticky === 'left'
                              ? '2px 0 4px -3px rgba(0, 0, 0, 0.35)'
                              : '-2px 0 4px -3px rgba(0, 0, 0, 0.35)',
                        }
                      : undefined
                  }
                  sortDirection={
                    orderBy === headCell.id ||
                    headCell.sortOptions?.some(option => option.id === orderBy)
                      ? order
                      : false
                  }
                  style={{
                    width: columnWidths[index] || headCell.width || 'auto',
                    minWidth:
                      headCell.width || (columnWidths[index] ? columnWidths[index] : '100px'),
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                  }}
                >
                  {headCell.sortOptions ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box>
                        <Typography component="div" sx={{ fontWeight: 'bold' }}>
                          {headCell.label}
                        </Typography>
                        {headCell.subtitle && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontWeight: 'normal' }}
                          >
                            {headCell.subtitle}
                          </Typography>
                        )}
                      </Box>
                      {headCell.tooltip && (
                        <Tooltip title={headCell.tooltip} arrow enterTouchDelay={0}>
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: 'text.secondary',
                              cursor: 'help',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                      <Tooltip
                        title={(() => {
                          const activeOption = headCell.sortOptions?.find(
                            option => option.id === orderBy
                          );
                          return activeOption
                            ? `Sorted by ${activeOption.label}, ${order === 'asc' ? 'ascending' : 'descending'}`
                            : `Sort ${headCell.label}`;
                        })()}
                      >
                        <IconButton
                          size="small"
                          aria-label={(() => {
                            const activeOption = headCell.sortOptions?.find(
                              option => option.id === orderBy
                            );
                            return activeOption
                              ? `Sorted by ${activeOption.label}, ${order === 'asc' ? 'ascending' : 'descending'}`
                              : `Sort ${headCell.label}`;
                          })()}
                          onClick={event => {
                            setSortMenuAnchor(event.currentTarget);
                            setSortMenuColumn(String(headCell.id));
                          }}
                        >
                          <SortIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Menu
                        anchorEl={sortMenuAnchor}
                        open={sortMenuColumn === String(headCell.id)}
                        onClose={() => {
                          setSortMenuAnchor(null);
                          setSortMenuColumn(null);
                        }}
                      >
                        {headCell.sortOptions.flatMap(option =>
                          (['asc', 'desc'] as const).map(selectedOrder => (
                            <MenuItem
                              key={`${option.id}-${selectedOrder}`}
                              selected={orderBy === option.id && order === selectedOrder}
                              onClick={() => handleSelectSort(option.id, selectedOrder)}
                            >
                              {option.label} —{' '}
                              {selectedOrder === 'asc' ? 'ascending' : 'descending'}
                            </MenuItem>
                          ))
                        )}
                      </Menu>
                    </Box>
                  ) : headCell.sortable ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : 'asc'}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                      {headCell.tooltip && (
                        <Tooltip title={headCell.tooltip} arrow enterTouchDelay={0}>
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: 'text.secondary',
                              cursor: 'help',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  ) : headCell.label ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box>
                        <Typography component="div" sx={{ fontWeight: 'bold' }}>
                          {headCell.label}
                        </Typography>
                        {headCell.subtitle && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', fontWeight: 'normal' }}
                          >
                            {headCell.subtitle}
                          </Typography>
                        )}
                      </Box>
                      {headCell.tooltip && (
                        <Tooltip title={headCell.tooltip} arrow enterTouchDelay={0}>
                          <InfoOutlinedIcon
                            sx={{
                              fontSize: 16,
                              color: 'text.secondary',
                              cursor: 'help',
                              flexShrink: 0,
                            }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  ) : null}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.length > 0 ? (
              paginatedData.map(row => {
                const rowId = idAccessor(row);
                const isSelected = selectedSet.has(rowId);
                const groupPath = groups.map(group => group.getValue(row));
                const firstChangedGroup = groupPath.findIndex(
                  (value, index) => value !== previousGroupPath[index]
                );
                previousGroupPath = groupPath;
                return (
                  <Fragment key={rowId}>
                    {firstChangedGroup >= 0 &&
                      groups.slice(firstChangedGroup).map((group, offset) => {
                        const level = firstChangedGroup + offset;
                        const value = groupPath[level];
                        const key = groupKey(groupPath, level);
                        const count = groupCounts.get(key) ?? 0;
                        const collapsed = collapsedGroups.has(key);
                        const hiddenByParent = groupPath
                          .slice(0, level)
                          .some((_, parentLevel) =>
                            collapsedGroups.has(groupKey(groupPath, parentLevel))
                          );
                        if (hiddenByParent) return null;
                        return (
                          <TableRow key={`${String(rowId)}-${group.id}-${value}`}>
                            <TableCell
                              colSpan={totalColumns}
                              sx={{
                                py: level === 0 ? 1.25 : 0.75,
                                pl: 2 + level * 3,
                                bgcolor: level === 0 ? 'action.hover' : 'grey.50',
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${group.label}: ${
                                    group.valueLabels?.[value] ?? value
                                  }`}
                                  aria-expanded={!collapsed}
                                  onClick={() => handleToggleGroupCollapse(key)}
                                >
                                  {collapsed ? (
                                    <KeyboardArrowRightIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                                <Box>
                                  <Typography
                                    variant={level === 0 ? 'subtitle1' : 'subtitle2'}
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {group.label}: {group.valueLabels?.[value] ?? value} ({count})
                                  </Typography>
                                  {group.valueSubtitles?.[value] && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: 'block' }}
                                    >
                                      {group.valueSubtitles[value]}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {!groupPath.some((_, level) =>
                      collapsedGroups.has(groupKey(groupPath, level))
                    ) && (
                      <TableRow selected={selectable && isSelected}>
                        {selectable && (
                          <TableCell padding="checkbox">
                            <Checkbox
                              color="primary"
                              checked={isSelected}
                              disabled={isRowSelectable ? !isRowSelectable(row) : false}
                              onChange={() => toggleRow(rowId, row)}
                              inputProps={{ 'aria-label': `select row ${rowId}` }}
                            />
                          </TableCell>
                        )}
                        {headCells.map((headCell, index) => (
                          <TableCell
                            key={`${rowId}-${String(headCell.id)}`}
                            align={'left'}
                            sx={
                              headCell.sticky
                                ? {
                                    position: 'sticky',
                                    [headCell.sticky]: 0,
                                    zIndex: 2,
                                    bgcolor: 'background.paper',
                                    boxShadow:
                                      headCell.sticky === 'left'
                                        ? '2px 0 4px -3px rgba(0, 0, 0, 0.35)'
                                        : '-2px 0 4px -3px rgba(0, 0, 0, 0.35)',
                                  }
                                : undefined
                            }
                            style={{
                              width: columnWidths[index] || headCell.width || 'auto',
                              minWidth:
                                headCell.width ||
                                (columnWidths[index] ? columnWidths[index] : '100px'),
                            }}
                          >
                            {headCell.renderCell
                              ? headCell.renderCell(row)
                              : (row[headCell.id as keyof T] as ReactNode)}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={totalColumns} align="center">
                  <Typography variant="body1">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 2, alignItems: 'center' }}>
          <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="rows-per-page-label">Rows per page</InputLabel>
            <Select
              labelId="rows-per-page-label"
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage as any}
              label="Rows per page"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>

          <Pagination
            count={pageCount}
            page={currentPage}
            onChange={handleChangePage}
            color="primary"
            showFirstButton
            showLastButton
          />

          <Typography variant="body2" color="text.secondary">
            {`Page ${currentPage} of ${pageCount}`}
          </Typography>
        </Box>
      )}
    </Paper>
  );
}
